import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, X, Tag as TagIcon, Check, Settings2, Trash2 } from 'lucide-react';

/**
 * Reusable multi-select tag picker backed by /api/comment-tags.
 *
 * - `scope` is "tenant" or "agent" (drives which tag library is loaded).
 * - `value` is an array of selected label strings (server stores labels,
 *   NOT ObjectIds — matches Trevor's legacy Access export style).
 * - `onChange(next: string[])` is fired when the user toggles a tag or
 *   adds/removes one inline. Persisting to the property is the parent's
 *   responsibility (PATCH /properties/{id} { [field]: next }).
 * - "+ Add" creates a new tag in the shared library AND auto-selects it.
 * - The gear icon flips into "manage" mode where each tag exposes inline
 *   rename + soft-delete affordances; renames cascade across every
 *   property that already has the old label selected.
 */
export default function TagMultiSelect({ scope, value, onChange, label }) {
  const { api } = useAuth();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [manageMode, setManageMode] = useState(false);
  const [renameId, setRenameId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const loadTags = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/comment-tags?scope=${scope}`);
      setTags(Array.isArray(data) ? data : []);
    } catch { setTags([]); }
    setLoading(false);
  }, [api, scope]);

  useEffect(() => { loadTags(); }, [loadTags]);

  const selected = new Set((value || []).map(v => String(v).trim()).filter(Boolean));

  const toggle = (lbl) => {
    if (manageMode) return; // disable toggle while managing
    const next = new Set(selected);
    if (next.has(lbl)) next.delete(lbl);
    else next.add(lbl);
    onChange?.([...next]);
  };

  const addNew = async () => {
    const lbl = newLabel.trim();
    if (!lbl) return;
    try {
      await api.post('/comment-tags', { scope, label: lbl });
      setNewLabel('');
      setAdding(false);
      await loadTags();
      // Auto-select the new tag.
      if (!selected.has(lbl)) onChange?.([...selected, lbl]);
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not add tag');
    }
  };

  const beginRename = (tag) => {
    setRenameId(tag._id);
    setRenameValue(tag.label);
  };

  const saveRename = async (tag) => {
    const next = renameValue.trim();
    if (!next || next === tag.label) {
      setRenameId(null);
      return;
    }
    try {
      await api.patch(`/comment-tags/${tag._id}`, { label: next });
      // If this tag was currently selected on the property, swap the old
      // label for the new one in the parent's selection state so the
      // chip remains visibly selected after the cascade.
      if (selected.has(tag.label)) {
        const nextSet = new Set(selected);
        nextSet.delete(tag.label);
        nextSet.add(next);
        onChange?.([...nextSet]);
      }
      setRenameId(null);
      setRenameValue('');
      await loadTags();
    } catch (e) {
      alert(e.response?.data?.detail || 'Rename failed');
    }
  };

  const archiveTag = async (tag) => {
    if (!window.confirm(`Delete "${tag.label}" from the ${scope} tag library?\n\nProperties that already have it selected will keep the label as a dashed "archived" chip until cleared.`)) return;
    try {
      await api.delete(`/comment-tags/${tag._id}`);
      await loadTags();
    } catch (e) {
      alert(e.response?.data?.detail || 'Delete failed');
    }
  };

  // Selected labels no longer in the library (orphans) — show dashed.
  const libraryLabels = new Set(tags.map(t => t.label));
  const orphans = (value || []).filter(v => v && !libraryLabels.has(v));

  return (
    <div data-testid={`tag-multiselect-${scope}`} className="space-y-2">
      <div className="flex items-center gap-2">
        {label && (
          <p className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500 flex items-center gap-1">
            <TagIcon size={10} /> {label}
          </p>
        )}
        <button
          type="button"
          data-testid={`${scope}-tag-manage-toggle`}
          onClick={() => { setManageMode(m => !m); setRenameId(null); setAdding(false); }}
          className={`ml-auto text-[10px] font-bold uppercase tracking-[0.12em] flex items-center gap-1 px-1.5 py-0.5 rounded transition-colors ${manageMode ? 'bg-[#D35400] text-white' : 'text-slate-400 hover:text-[#D35400]'}`}
        >
          <Settings2 size={11} /> {manageMode ? 'Done' : 'Manage'}
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {loading ? (
          <span className="text-[11px] text-slate-400 italic">Loading tags…</span>
        ) : (
          <>
            {tags.map(t => {
              const on = selected.has(t.label);
              const isRenaming = renameId === t._id;
              if (manageMode) {
                return (
                  <span
                    key={t._id}
                    data-testid={`${scope}-tag-manage-row-${t._id}`}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border border-slate-300 bg-white"
                  >
                    {isRenaming ? (
                      <>
                        <input
                          data-testid={`${scope}-tag-rename-input`}
                          autoFocus
                          value={renameValue}
                          onChange={e => setRenameValue(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') { e.preventDefault(); saveRename(t); }
                            if (e.key === 'Escape') { setRenameId(null); }
                          }}
                          maxLength={60}
                          className="px-1 border border-[#D35400] rounded text-[11px] w-32"
                        />
                        <button onClick={() => saveRename(t)} className="text-emerald-600 hover:text-emerald-700"><Check size={11} /></button>
                        <button onClick={() => setRenameId(null)} className="text-slate-400 hover:text-slate-600"><X size={11} /></button>
                      </>
                    ) : (
                      <>
                        <span className="text-slate-700">{t.label}</span>
                        <button
                          data-testid={`${scope}-tag-rename-${t._id}`}
                          onClick={() => beginRename(t)}
                          title="Rename"
                          className="text-slate-400 hover:text-[#D35400]"
                        >✎</button>
                        <button
                          data-testid={`${scope}-tag-delete-${t._id}`}
                          onClick={() => archiveTag(t)}
                          title="Delete"
                          className="text-slate-400 hover:text-red-600"
                        ><Trash2 size={10} /></button>
                      </>
                    )}
                  </span>
                );
              }
              return (
                <button
                  key={t._id}
                  type="button"
                  data-testid={`${scope}-tag-${t.label.replace(/\s+/g, '-').toLowerCase()}`}
                  onClick={() => toggle(t.label)}
                  className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border transition-colors ${on
                    ? 'bg-[#D35400] text-white border-[#D35400]'
                    : 'bg-white text-slate-600 border-slate-300 hover:border-[#D35400]/40'}`}
                >
                  {on && <Check size={9} className="inline-block mr-0.5 -mt-0.5" strokeWidth={3} />}
                  {t.label}
                </button>
              );
            })}
            {!manageMode && orphans.map(o => (
              <button
                key={`orphan-${o}`}
                type="button"
                onClick={() => toggle(o)}
                title="Archived tag — click to remove"
                className="text-[11px] font-semibold px-2 py-0.5 rounded-full border border-dashed border-slate-400 bg-slate-50 text-slate-500 italic"
              >{o} <X size={9} className="inline-block ml-0.5 -mt-0.5" /></button>
            ))}
            {!manageMode && (adding ? (
              <span className="inline-flex items-center gap-1">
                <input
                  data-testid={`${scope}-tag-new-input`}
                  autoFocus
                  value={newLabel}
                  onChange={e => setNewLabel(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addNew(); } if (e.key === 'Escape') { setAdding(false); setNewLabel(''); } }}
                  placeholder="New tag…"
                  maxLength={60}
                  className="text-[11px] px-2 py-0.5 border border-[#D35400] rounded-full focus:outline-none w-32"
                />
                <button data-testid={`${scope}-tag-new-save`} type="button" onClick={addNew} className="text-emerald-600 hover:text-emerald-700"><Check size={12} /></button>
                <button type="button" onClick={() => { setAdding(false); setNewLabel(''); }} className="text-slate-400 hover:text-slate-600"><X size={12} /></button>
              </span>
            ) : (
              <button
                data-testid={`${scope}-tag-add-btn`}
                type="button"
                onClick={() => setAdding(true)}
                className="text-[11px] font-bold px-2 py-0.5 rounded-full border border-dashed border-[#D35400] text-[#D35400] hover:bg-[#FFF7ED]"
              >
                <Plus size={9} className="inline-block -mt-0.5" /> Add
              </button>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
